/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import React, {useContext, useEffect, useState} from 'react';
import Header from '../Header/Header';
import {ExdsButton, ExdsGrid, ExdsLayout, ExdsSpacing, ExdsText} from '@exds/core';
import './SecurityQuestions.scss';
import QuestionsSection from './QuestionsSection';
import StartOverModal from '../StartOverModal/StartOverModal';
import {apiPost} from '../../service/service';
import {doEncrypt} from '../E2ee/E2ee';
import AppContext from "../../AppContext";
import Alert from '../Alert/Alert';
import {TuxLoadingIndicator} from '@tuxedo/react-component-library';
import {useNavigate} from 'react-router-dom';
import {ALPHANUMERIC_WITH_SPACES_REGEX, SOMETHING_WENT_WRONG} from "../../constants/constants";
import Error from "../Error/Error";
import {addPageLoadEvent} from "../AdobeAnalytics/AdobeAnalytics";
import {isNullOrEmpty, returnLandingPage} from "../../utils/utils";

export default function SecurityQuestions() {
  const {sidContext, phoneNumberContext, hashContext, transactionIdContext, expirationTimeContext, loadAA, isSoftTokenEnabledContext, isSmartLogonEnabledContext} = useContext(AppContext);
  const sid = sidContext.at(0);
  const phoneNumber = phoneNumberContext.at(0);
  const transactionId = transactionIdContext.at(0);
  const expirationTime = expirationTimeContext.at(0);
  const [hash, setHash] = hashContext;
  const isSoftTokenEnabledBool = isSoftTokenEnabledContext.at(0) === "true";
  const isSmartLogonEnabledBool = isSmartLogonEnabledContext.at(0) === "true";
  const [hasAnswerError, setHasAnswerError] = useState(false);
  const [hasQuestionError, setHasQuestionError] = useState(false);
  const [questionAnswersSelected, setQuestionAnswersSelected] = useState(Array(6).fill({id: null, answer: null}));
  const [correlationId, setCorrelationId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answersTouched, setAnswersTouched] = useState([false, false, false, false, false, false]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isStartOverModalOpen, setIsStartOverModalOpen] = useState(false);
  const pageName = 'Security Questions';

  useEffect(() => {
    if(loadAA) {
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]);

  useEffect(() => {
      async function getSecurityQuestions() {
        try {
          setIsLoading(true);
          const response = await apiPost('/api/approvedSecurityQuestions', {
            sid,
            phoneNumber,
            transactionId,
            hash,
            expirationTime
          });
          if (response.status === 200) {
            const retrievedQuestions = response.data.questions;
            if (retrievedQuestions.length !== 0) {
              setHash(response.data.hash);
              setQuestions(retrievedQuestions);
            } else {
              setCorrelationId(response.correlationId);
              setHasQuestionError(true);
            }
          } else {
            setCorrelationId(response.correlationId);
            setHasQuestionError(true);
          }
        } catch (err) {
          setCorrelationId(err.correlationId);
          setHasQuestionError(true);
        } finally {
          setIsLoading(false);
        }
      }

      if (isNullOrEmpty(hash)) {
        returnLandingPage();
      } else {
        getSecurityQuestions();
      }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function buildSaveAnswersRequestPayload({questionAnswersSelected, doEncrypt}) {
    return {
      sid,
      answers: await Promise.all(
        questionAnswersSelected.map(async (qa) => ({
          questionId: qa.id,
          answer: await doEncrypt(qa.answer),
        }))
      ),
      phoneNumber,
      transactionId,
      hash,
      expirationTime
    };
  }

  async function saveAnswers(requestPayload) {
    try {
      const response = await apiPost('/api/securityQuestions', requestPayload);
      setFormSubmitted(false);
      setIsLoading(false);
      if (response.status === 200) {
        setHash(response.data.hash);
        setHasAnswerError(false);
        if(isSoftTokenEnabledBool || isSmartLogonEnabledBool) {
          navigate('/mobileAppSetup');
        } else {
          navigate('/finish');
        }
      } else {
        setCorrelationId(response.correlationId);
        setHasAnswerError(true);
      }
    } catch (err) {
      setCorrelationId(err.correlationId);
      setHasAnswerError(true);
    }

  }

  useEffect(() => {
    if (formSubmitted) {
      setTimeout(() => {
        const err = document.querySelector('.validation-error input');
        if (err) {
          err.focus();
        }
      }, 0);
    }
  }, [formSubmitted]);

  const hasValidationErrors = () => {
    return questionAnswersSelected.some(
      (qa, index) =>
        !qa.answer ||
        qa.answer.trim().length < 3 ||
        qa.answer.length > 255 ||
        questionAnswersSelected.some((otherQa, otherIndex) => otherIndex !== index && otherQa.answer === qa.answer) ||
        !qa.answer.match(ALPHANUMERIC_WITH_SPACES_REGEX)
    );
  };

  const handleSubmitClick = async () => {
    setFormSubmitted(true);

    if (!hasValidationErrors()) {
      const requestPayload = await buildSaveAnswersRequestPayload({questionAnswersSelected, doEncrypt});
      setIsLoading(true);
      await saveAnswers(requestPayload);
    }
  };

  return (
    <>
      <Header userValidated currentStep={3} pageName={pageName} sid={sid} />
      <div className={`${isLoading ? 'loading-spinner':'hide-it'}`}><TuxLoadingIndicator /></div>
      {hasQuestionError &&
        <Error
          content={SOMETHING_WENT_WRONG}
          buttonTitle="Start over"
          buttonLink="/validateIdentity"
          correlationId={correlationId}
          pageName={pageName}
          sid={sid}
        /> }
      <ExdsGrid container className={`${(isLoading || hasQuestionError ) ? 'hide-it' : 'security-questions-container'}`}>
        <ExdsLayout
          display="flex"
          alignItems="flex-start"
          flexDirection="column"
        >
          {hasAnswerError && <Alert correlationId={correlationId} loadAA={loadAA} pageName={pageName} sid={sid}/>}
          <ExdsText
            primitive="h1"
            textStyle="titleExtraLarge"
            className="mb-075"
          >
            Security Questions
          </ExdsText>
          <ExdsText textStyle="bodyMedium" className="sid-label">
            <b>Standard ID (SID):</b>&nbsp;<span className="fw-400">{sid}</span>
          </ExdsText>
          <ExdsSpacing margin={{top: '1rem'}}>
            <ExdsText primitive="div">
              For additional security, we also require you to choose <b>6 different security questions</b> and provide
              unique answers for each. These security questions will be used to help us automatically confirm your
              identity in the future.
            </ExdsText>
          </ExdsSpacing>
          <ExdsSpacing margin={{top: '1rem'}}>
            <ExdsText primitive="div">
              <p><b>Your security answers:</b></p>
              <ul>
                <li>Must be between 3 to 255 alphanumeric and blank characters</li>
                <li>Are case sensitive</li>
                <li>Must be unique (cannot have the same answer more than once)</li>
              </ul>
            </ExdsText>
          </ExdsSpacing>
          <div className='question-answers-container'>
            {[...Array(6)].map((el, i) => (
              (i === 0 || (questionAnswersSelected[i - 1].id && answersTouched[i - 1])) &&
                <QuestionsSection
                  key={i}
                  questions={questions}
                  questionIndex={i}
                  setQuestions={setQuestions}
                  setQuestionAnswersSelected={setQuestionAnswersSelected}
                  formSubmitted={formSubmitted}
                  questionAnswersSelected={questionAnswersSelected}
                  setAnswersTouched={setAnswersTouched}
                  answersTouched={answersTouched}
                />
            ))}
          </div>
          <div className="buttons-container">
            <ExdsButton
              text="Start over"
              variant="secondary"
              className="action-button"
              onClick={() => setIsStartOverModalOpen(true)}
            />
            {questionAnswersSelected[5].id !== null && <ExdsButton
              text="Submit"
              className="action-button"
              onClick={handleSubmitClick}
            />}
          </div>
        </ExdsLayout>
        <StartOverModal isStartOverModalOpen={isStartOverModalOpen} setIsStartOverModalOpen={setIsStartOverModalOpen} />
      </ExdsGrid>
    </>
  );
}
